/* eslint-disable prefer-destructuring */
/* eslint-disable complexity */
/* eslint-disable max-lines */
import { DSButtonV2 } from '@elliemae/ds-button-v2';
import { Grid } from '@elliemae/ds-grid';
import { css, styled, type Theme } from '@elliemae/ds-system';
import { DSControlledDateTimePickerName, DSControlledDateTimePickerSlots } from '../../../constants/index.js';

interface StyledWheelListItemPropsT {
  $isTheItemVisuallyInTheCenter?: boolean;
  $selected?: boolean;
  $isNotAValidSelection?: boolean;
  $readOnly?: boolean;
  $isUnset?: boolean;
  $isFocusedWheel?: boolean;
}

interface StyledTimePickerContainerT {
  showShadow?: boolean;
  isControllerOnly: boolean;
}

// Focus ring — always brand-700 regardless of validity or readOnly state.
// Only rendered for the visually centred item when the wheel is focused and the item is not selected.
const focusBorder = (props: StyledWheelListItemPropsT & { theme: Theme }) => {
  const { theme, $isTheItemVisuallyInTheCenter, $selected, $isFocusedWheel } = props;
  if ($isTheItemVisuallyInTheCenter && !$selected && $isFocusedWheel)
    return css<StyledWheelListItemPropsT>`
      &:after {
        position: absolute;
        content: '';
        top: -2px;
        left: -2px;
        width: calc(100% + 4px);
        height: calc(100% + 4px);
        border-radius: 50%;
        border: 2px solid ${theme.colors.brand[700]};
      }
    `;
  return css``;
};

// Diagonal line for invalid items. Colour is neutral at rest in all states —
// the focus ring (::after) is the sole blue/brand focus signal.
const diagonalLine = (props: StyledWheelListItemPropsT & { theme: Theme }) => {
  const { theme, $selected, $isNotAValidSelection, $readOnly } = props;
  if (!$isNotAValidSelection) return css``;

  let beforeBgColor = theme.colors.neutral[500]; // idle not-selected
  if ($readOnly && !$selected) beforeBgColor = theme.colors.neutral[700]; // readOnly not-selected
  if ($selected) beforeBgColor = theme.colors.neutral['000']; // selected always white

  return css<StyledWheelListItemPropsT>`
    cursor: not-allowed;
    &:before {
      position: absolute;
      content: '';
      top: 48%;
      left: -1px;
      height: 1px;
      width: calc(100% + 2px);
      transform: rotate(45deg);
      background-color: ${beforeBgColor};
    }
  `;
};

// Text colour. On focus the invalid case resolves to brand-700 (not danger-900) for
// consistency with the universal blue focus ring decision.
const fontColor = (props: StyledWheelListItemPropsT & { theme: Theme }) => {
  const { theme, $selected, $readOnly, $isNotAValidSelection, $isFocusedWheel, $isTheItemVisuallyInTheCenter } = props;

  let color = theme.colors.neutral[700]; // default
  if ($selected) color = theme.colors.neutral['000']; // selected always white

  // For items that can't receive :focus (meridiem visual layer), drive focused-center colour via props.
  // Hours/minutes also benefit from this — :focus below applies the same value.
  if ($isTheItemVisuallyInTheCenter && $isFocusedWheel && !$selected && !$readOnly) {
    color = theme.colors.brand[700]; // always brand, matches the blue focus ring
  }

  // Hover: valid not-selected not-readOnly → brand-700; all other cases → no change
  const hoverColor = !$selected && !$readOnly && !$isNotAValidSelection ? theme.colors.brand[700] : '';

  // Focus (:focus fires on hours/minutes; meridiem relies on the prop-driven path above)
  // Matches focus ring: always brand when interactive, no change when readOnly or selected
  const focusColor = !$selected && !$readOnly ? theme.colors.brand[700] : '';

  return css<StyledWheelListItemPropsT>`
    color: ${color};
    &:hover {
      color: ${hoverColor};
    }
    &:focus {
      color: ${focusColor};
    }
  `;
};

// Background colours at rest and on hover.
// Hover matrix (readOnly always wins):
//   readOnly  → selected: neutral-700 | not-selected: neutral-100
//   invalid   → selected: danger-950  | not-selected: danger-200 hardcoded to #fffcfc due to contrast failure of danger-200 token
//   valid     → selected: brand-800   | not-selected: brand-100
const btnBgColorPerStatus = (props: StyledWheelListItemPropsT & { theme: Theme }) => {
  const { theme, $selected, $isNotAValidSelection, $readOnly, $isFocusedWheel, $isTheItemVisuallyInTheCenter } = props;

  // Rest background: only selected items have a non-transparent background.
  let bgColor = '';
  if ($selected) {
    bgColor = theme.colors.brand[600];
    if ($isNotAValidSelection) bgColor = theme.colors.danger[900]; // invalid selected: red
    if ($readOnly) bgColor = theme.colors.neutral[500]; // readOnly always wins: grey
  }

  // When the wheel column is active (focused), drive the selected centre item to the
  // same colour as its hover state — :hover won't fire on keyboard navigation and
  // meridiem visual items can't receive :focus at all.
  if ($isTheItemVisuallyInTheCenter && $isFocusedWheel && $selected) {
    if ($readOnly) bgColor = theme.colors.neutral[700];
    else if ($isNotAValidSelection) bgColor = theme.colors.danger[950];
    else bgColor = theme.colors.brand[800];
  }

  // Hover background — new spec, clean if/else, no empty-string leaks.
  let hoverBgColor: string;
  if ($readOnly) {
    hoverBgColor = $selected ? theme.colors.neutral[700] : theme.colors.neutral[100];
  } else if ($isNotAValidSelection) {
    // TODO: danger[200] and danger[400] are currently the same token and both fail contrast
    // against common dimsum color combinations. Hardcoded until the theme token is fixed.
    hoverBgColor = $selected ? theme.colors.danger[950] : '#fffcfc'; // this is hardcoded because danger 200 fails contrast, pending theme token fix
  } else {
    hoverBgColor = $selected ? theme.colors.brand[800] : theme.colors.brand[100];
  }

  return css`
    background-color: ${bgColor};
    &:hover {
      background-color: ${hoverBgColor};
    }
  `;
};

const cursor = (props: StyledWheelListItemPropsT & { theme: Theme }) => {
  const cursorStyle = props.$readOnly || props.$isNotAValidSelection ? 'not-allowed' : 'pointer';
  return css`
    cursor: ${cursorStyle};
  `;
};

// Font weight: selected → semibold at rest (no hover override needed, it is already semibold).
// Not selected → semibold on hover only.
const fontWeight = (props: StyledWheelListItemPropsT & { theme: Theme }) => {
  const { theme, $selected } = props;
  if ($selected) {
    return css`
      font-weight: ${theme.fontWeights.semibold};
    `;
  }
  return css`
    &:hover {
      font-weight: ${theme.fontWeights.semibold};
    }
  `;
};

export const StyledIconTriggerButton = styled(DSButtonV2, {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.PICKER_ICONS.TIMEWHEEL,
})`
  color: ${({ theme }) => theme.colors.brand['800']};
  width: 2rem;
  height: 2rem;
`;

export const StyledWheelList = styled.div`
  width: 100%;
  height: 100%;
  margin: auto;
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  &:focus-within {
    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 2px;
      border: 2px solid ${({ theme }) => theme.colors.brand[700]};
      box-shadow: 0 0 2px ${({ theme }) => theme.colors.brand[700]};
    }
  }
`;

export const StyledTimePickerContainer = styled('div', {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.CONTROLLER_COMPONENT.TIMEWHEEL,
})<StyledTimePickerContainerT>`
  display: grid;
  position: relative;
  grid-template-columns: 192px;
  grid-template-rows: 36px 30px auto;
  grid-template-areas:
    'TimePickerHead'
    'TimePickerWheelsLegend'
    'VerticalWheelWrapper';
`;

export const StyledTimePickerHead = styled(Grid, {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.TIMEWHEEL.HEADER_LABEL,
})`
  width: 100%;
  grid-area: TimePickerHead;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.neutral['050']};
`;

export const StyledTimePickerWheelsLegend = styled.div`
  width: 100%;
  height: 100%;
  grid-area: TimePickerWheelsLegend;
  display: grid;
  grid-template-columns: ${({ theme }) => theme.space.xl} ${({ theme }) => theme.space.xl} ${({ theme }) =>
    theme.space.xl};
  align-items: center;
  justify-content: center;
  text-align: center;
  background: ${({ theme }) => theme.colors.neutral['050']};
`;

export const StyledTimePickerWheelWrapper = styled.div<{ $readOnlyStyles?: boolean }>`
  background-color: ${({ theme, $readOnlyStyles }) =>
    $readOnlyStyles ? theme.colors.neutral['050'] : theme.colors.neutral['000']};
  width: 100%;
  grid-area: VerticalWheelWrapper;
  display: grid;
  grid-template-columns: ${({ theme }) => theme.space.xl} ${({ theme }) => theme.space.xl} ${({ theme }) =>
    theme.space.xl};
  grid-template-rows: 100%;
  & ul:not(:last-child) {
    position: relative;
    &:after {
      content: '';
      position: absolute;
      left: 100%;
      top: 5%;
      height: 90%;
      width: 1px;
      background: ${({ theme }) => theme.colors.neutral['300']};
    }
  }

  > *:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  }
`;

export const StyledTimeBtn = styled(Grid, {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.TIMEWHEEL.TIME_BUTTON,
})<StyledWheelListItemPropsT>`
  position: relative;
  height: ${({ theme }) => theme.space.s};
  width: ${({ theme }) => theme.space.s};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  &:focus {
    outline: none;
  }
  ${cursor}
  ${fontColor}
  ${diagonalLine}
  ${btnBgColorPerStatus}
  ${focusBorder}
  ${fontWeight}
`;

export const StyledWheelListItem = styled.div`
  margin: auto;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

export const StyledWheelChangeTimeBtn = styled(DSButtonV2, {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.TIMEWHEEL.TIME_CHANGE_BUTTON,
})`
  height: ${({ theme }) => theme.space.s};
  width: ${({ theme }) => theme.space.s};
  min-width: ${({ theme }) => theme.space.s};
  border-radius: 50%;
`;

export const TimeWheelFooterMessage = styled('div', {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.FOOTERS.TIMEWHEEL,
})<{ $readOnlyStyles?: boolean }>`
  border-top: solid 1px ${({ theme }) => theme.colors.neutral[300]};
  padding: ${({ theme }) => theme.space.xxs2};
  color: ${({ theme }) => theme.colors.danger[900]};
  font-size: ${({ theme }) => theme.fontSizes.microText[200]};
  text-align: right;
  background-color: ${({ theme, $readOnlyStyles }) =>
    $readOnlyStyles ? theme.colors.neutral['050'] : theme.colors.neutral['000']};
`;
